// ✅ FIX: Ensure horoscope change reflects in Dashboard immediately

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import defaultAvatar from '../assets/default-avatar.png';
import "../styles/ProfilePage.css";
import { UserContext } from '../UserContext';

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const calcBMI = (heightCm, weightKg) => {
  const h = toNum(heightCm);
  const w = toNum(weightKg);
  if (!h || !w) return 0;
  const m = h / 100;
  return +(w / (m * m)).toFixed(1);
};

function ProfilePage() {
  const [editMode, setEditMode] = useState(false);
  const [profilePic, setProfilePic] = useState(defaultAvatar);
  const { updateProfilePic } = useContext(UserContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    dob: '',
    height: '',
    weight: '',
    gender: '',
    horoscope: ''
  });

  useEffect(() => {
    const name = localStorage.getItem('userName') || '';
    const dob = localStorage.getItem('userDOB') || '';
    const height = localStorage.getItem('userHeight') || '';
    const weight = localStorage.getItem('userWeight') || '';
    const gender = localStorage.getItem('userGender') || '';
    const horoscope = localStorage.getItem('userHoroscope') || '';
    const pic = localStorage.getItem('userPic') || defaultAvatar;

    setFormData({ name, dob, height, weight, gender, horoscope });
    setProfilePic(pic);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
  const userProfile = {
    name: formData.name,
    dob: formData.dob,
    height: formData.height,
    weight: formData.weight,
    gender: formData.gender,
    horoscope: formData.horoscope,
    profilePic,
    email: localStorage.getItem('userEmail'),
    password: localStorage.getItem('userPassword')
  };

  // ✅ Save updated data to localStorage
  localStorage.setItem('userProfile', JSON.stringify(userProfile));
  localStorage.setItem('userName', formData.name);
  localStorage.setItem('userDOB', formData.dob);
  localStorage.setItem('userHeight', formData.height);
  localStorage.setItem('userWeight', formData.weight);
  localStorage.setItem('userGender', formData.gender);
  localStorage.setItem('userHoroscope', formData.horoscope);
  localStorage.setItem('horoscope', formData.horoscope); // ✅ used by DashboardBar
  updateProfilePic(profilePic);

  const bmi = calcBMI(formData.height, formData.weight);
  const bodyFat = Math.max(10, Math.min(50, +(bmi * 1.2).toFixed(1)));
  const muscleMass = Math.max(30, +(70 - bmi * 0.5).toFixed(1));
  const boneMass = +(2 + bmi * 0.03).toFixed(2);
  const waterContent = Math.max(35, Math.min(65, +(70 - bmi).toFixed(1)));

  localStorage.setItem('lastBMI', String(bmi));
  localStorage.setItem('dashboardMetrics', JSON.stringify({ bmi, bodyFat, muscleMass, boneMass, waterContent }));

  try {
    const token = localStorage.getItem("token");
    const email = localStorage.getItem("userEmail");
    const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://health-ai-zfr7.onrender.com';

    await fetch(`${apiBaseUrl}/api/update-profile`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        email,
        name: formData.name,
        dob: formData.dob,
        height: formData.height,
        weight: formData.weight,
        gender: formData.gender,
        horoscope: formData.horoscope,
        profilePic,
      }),
    });
  } catch (err) {
    console.error("Failed to update MongoDB profile:", err);
  }

  // ✅ Notify components
  window.dispatchEvent(new Event('profile-updated'));
  window.dispatchEvent(new Event('storage'));

  setEditMode(false);

  // ✅ Show alert
  alert("Profile successfully updated!");

  // ✅ Redirect to homepage
  navigate('/');
};

  const handleProfilePicClick = () => {
    document.getElementById('fileInput').click();
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "ml_default");

      const res = await fetch("https://api.cloudinary.com/v1_1/dxhl6yw4i/image/upload", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (data.secure_url) {
        const imageUrl = data.secure_url;
        setProfilePic(imageUrl);
        localStorage.setItem('userPic', imageUrl);
        updateProfilePic(imageUrl);
        window.dispatchEvent(new Event('profile-updated'));
      } else {
        console.error("Upload failed, no secure_url returned:", data);
      }
    } catch (err) {
      console.error("Failed to upload image to Cloudinary:", err);
    }
  };

  return (
    <div className="profile-container">
      <div className="profile-box large-box">
        <img
          src={profilePic || defaultAvatar}
          alt="Profile"
          className="profile-img"
          onClick={handleProfilePicClick}
          style={{ cursor: 'pointer' }}
          onError={(e) => (e.currentTarget.src = defaultAvatar)}
        />
        <input
          id="fileInput"
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleImageChange}
        />

        {editMode ? (
          <>
            <input name="name" value={formData.name} onChange={handleChange} className="profile-input" />
            <input type="date" name="dob" value={formData.dob} onChange={handleChange} className="profile-input" />
            <input name="height" value={formData.height} onChange={handleChange} className="profile-input" />
            <input name="weight" value={formData.weight} onChange={handleChange} className="profile-input" />
            <select name="gender" value={formData.gender} onChange={handleChange} className="profile-input">
              <option value="">Select Gender</option>
              <option>Male</option>
              <option>Female</option>
              <option>Other</option>
            </select>
            <select name="horoscope" value={formData.horoscope} onChange={handleChange} className="profile-input">
              <option value="">Select Zodiac Sign</option>
              <option>Aries</option>
              <option>Taurus</option>
              <option>Gemini</option>
              <option>Cancer</option>
              <option>Leo</option>
              <option>Virgo</option>
              <option>Libra</option>
              <option>Scorpio</option>
              <option>Sagittarius</option>
              <option>Capricorn</option>
              <option>Aquarius</option>
              <option>Pisces</option>
            </select>
            <button className="edit-btn" onClick={handleSave}>Save</button>
          </>
        ) : (
          <>
            <div className="profile-detail"><strong>Name:</strong> {formData.name}</div>
            <div className="profile-detail"><strong>Date of Birth:</strong> {formData.dob}</div>
            <div className="profile-detail"><strong>Height:</strong> {formData.height} cm</div>
            <div className="profile-detail"><strong>Weight:</strong> {formData.weight} kg</div>
            <div className="profile-detail"><strong>Gender:</strong> {formData.gender}</div>
            <div className="profile-detail"><strong>Zodiac Sign:</strong> {formData.horoscope}</div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <button className="edit-btn" onClick={() => setEditMode(true)}>Edit Profile</button>
              <button className="edit-btn" onClick={handleSave}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ProfilePage;